import { useEffect } from 'react'

import { useUser } from '../../../../common/context/User'
import { trackUpgradePathViewed } from '../../../../common/hooks/useSegmentTracking'
import UpgradePathsTracking from '../../../../tracking/UpgradePaths'
import { useModalManager } from '../../../ModalManager/hooks/useModalManager'

const DEFAULT_CTA_DATA = {
  cta: 'annualUpgradeModal-annualUpgradeModal',
  ctaView: 'annualUpgradeModal',
  ctaLocation: 'annualUpgradeModal',
  ctaVersion: '1',
  upgradePathName: 'annualUpgrade',
}

type AnnualUpgradeTracking = {
  trackSwitchToAnnualClicked: () => void
  trackKeepMonthlyClicked: () => void
  /** Resolved CTA + upgrade path for this modal, so the mutation can send the same attribution we track. */
  cta: string
  upgradePathName: string
}

export function useAnnualUpgradeTracking(): AnnualUpgradeTracking {
  const user = useUser()
  const { modalData } = useModalManager()
  const { trackCTAClicked } = UpgradePathsTracking(user)

  const ctaData = {
    cta: modalData?.cta ?? DEFAULT_CTA_DATA.cta,
    ctaView: modalData?.ctaView ?? DEFAULT_CTA_DATA.ctaView,
    ctaLocation: modalData?.ctaLocation ?? DEFAULT_CTA_DATA.ctaLocation,
    ctaVersion: modalData?.ctaVersion ?? DEFAULT_CTA_DATA.ctaVersion,
    upgradePathName:
      modalData?.upgradePathName ?? DEFAULT_CTA_DATA.upgradePathName,
  }

  useEffect(() => {
    trackUpgradePathViewed({
      payload: {
        upgradePathName: ctaData.upgradePathName,
        cta: ctaData.cta,
      },
      user,
    })
    // Intentionally fire-once on mount — adding deps would cause duplicate tracking events
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const trackSwitchToAnnualClicked = (): void => {
    trackCTAClicked({
      ctaView: ctaData.ctaView,
      ctaLocation: ctaData.ctaLocation,
      ctaButton: 'switchToAnnual',
      upgradePathName: ctaData.upgradePathName,
    })
  }

  const trackKeepMonthlyClicked = (): void => {
    trackCTAClicked({
      ctaView: ctaData.ctaView,
      ctaLocation: ctaData.ctaLocation,
      ctaButton: 'keepMonthly',
      upgradePathName: ctaData.upgradePathName,
    })
  }

  return {
    trackSwitchToAnnualClicked,
    trackKeepMonthlyClicked,
    cta: ctaData.cta,
    upgradePathName: ctaData.upgradePathName,
  }
}
